import { logger, logD, LoggingConfig } from "@blacknut/logging/dist";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { focusSectionWithPath } from "./FocusableSection";
import { inputUI } from "./input";
import JsSpatialNavigation from "./lib/spatial_navigation.js";

interface SpatialNavigationConfig {
  activeClassName: string;
  focusableClassName: string;
  selector: string;
}

const defaultConfig = {
  activeClassName: "active",
  focusableClassName: "focusable",
  selector: ".focusable",
};
const NOP = () => {
  //
};

const INSTANCE_ID = [0];

export const SpatialNavigationContext = React.createContext<{
  config: SpatialNavigationConfig;
  pause: () => void;
  resume: () => void;
  active: boolean;
  enabled: boolean;
  teardown: () => void;
}>({
  config: defaultConfig,
  active: false,
  enabled: false,
  pause: NOP,
  resume: NOP,
  teardown: NOP,
});

const MouseEvents: (keyof HTMLElementEventMap)[] = [
  "mousemove",
  "touchstart",
  "click",
];
const KeyboardKeyCodes = [13, 27, 37, 38, 39, 40];
const SpatialNavigationProvider = ({
  children,
  defaultEnabled = false,
  defaultActive = true,
  loggingConfig,
}: PropsWithChildren<{
  defaultEnabled?: boolean;
  defaultActive?: boolean;
  loggingConfig?: LoggingConfig;
}>) => {
  const [config] = useState<SpatialNavigationConfig>(defaultConfig);
  const [enabled, setEnabled] = useState(defaultEnabled);
  const [active, setActive] = useState(defaultActive);

  const gameModeRef = useRef(false);
  const lastMouseMoveXY = useRef([0, 0]);
  const mounted = useRef(false);
  const TAG = useRef(`SpatialNav#${INSTANCE_ID[0]++}`).current;

  useEffect(() => {
    logD(TAG, "mount");
    mounted.current = true;
    return () => {
      logD(TAG, "umount");
      mounted.current = false;
    };
  }, []);

  const _pause = useCallback(() => {
    logD(TAG, "In _pause");

    JsSpatialNavigation.pause();
    if (document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
  }, []);

  const _resume = useCallback(() => {
    logD(TAG, "In _resume");
    if (inited.current) {
      JsSpatialNavigation.resume();
    } else {
      inited.current = true;
      JsSpatialNavigation.init(config);
    }
    JsSpatialNavigation.focus();
  }, []);
  const disable = useCallback(() => {
    setEnabled((b) => {
      logD(TAG, "In disable. old enabled=%o", b);
      if (b) {
        logD(TAG, "Changed");
      } else {
        logD(TAG, "Unchanged");
      }
      return false;
    });
  }, []);

  useEffect(() => {
    logD(TAG, "In use effect. Active=%o, Enabled=%o", TAG, active, enabled);
    if (enabled && active) {
      _resume();
    } else if (!enabled || !active) {
      _pause();
    }
  }, [enabled, active, _resume]);

  const enable = useCallback(() => {
    setEnabled((b) => {
      logD(TAG, "In enable. old enabled=%o", b);
      if (!b) {
        logD(TAG, "Changed");
      } else {
        logD(TAG, "Unchanged");
      }
      return true;
    });
  }, [config]);

  const pause = useCallback(() => {
    setActive(false);
  }, []);

  const resume = useCallback(() => {
    setActive(true);
  }, []);

  useEffect(() => {
    const obs = inputUI.onGameModeChange().subscribe({
      next: (b) => {
        logD(TAG, "On game mode change", b);
        gameModeRef.current = b;
        if (b) {
          disable();
          inputUI.destroyGamepadUI();

          //Even if not active, keyDown handler is binded (in order to activate spatial nav on keydown)
          //So remove it here
          //When game mode will set back to false, keyDown handler will be binded by the setActive
          document.documentElement.removeEventListener(
            "keydown",
            onKeyDownEnable
          );
        } else {
          enable();
          inputUI.setupUIGamepad();
        }
      },
    });
    return () => {
      obs.unsubscribe();
    };
  }, [enable, disable]);

  const onMouseEventDisable = useCallback(
    (e: MouseEvent) => {
      if (
        Math.abs(lastMouseMoveXY.current[0] - e.x) > 30 ||
        Math.abs(lastMouseMoveXY.current[1] - e.y) > 30
      ) {
        logD(TAG, "onMouseEventDisable");

        disable();
      }
      lastMouseMoveXY.current = [e.x, e.y];
    },
    [disable]
  );

  const onKeyDownEnable = useCallback(
    (e: KeyboardEvent) => {
      if (
        KeyboardKeyCodes.indexOf(e.keyCode) !== -1 &&
        (e.target as HTMLElement)?.nodeName.toLowerCase() !== "input" &&
        (e.target as HTMLElement)?.nodeName.toLowerCase() !== "textarea"
      ) {
        logD(TAG, "onKeyDownEnable", e);
        enable();
      }
    },
    [enable]
  );

  useEffect(() => {
    logD(TAG, ">>In use effect. Enabled=%o", enabled);
    if (enabled) {
      logD(TAG, "Add mouse event handlers");
      MouseEvents.forEach((evt) =>
        document.documentElement.addEventListener(
          evt,
          onMouseEventDisable as any
        )
      );
      logD(TAG, "Remove key event handler");
      document.documentElement.removeEventListener("keydown", onKeyDownEnable);
      return () => {
        logD(TAG, "<<Out use effect. Enabled=%o", enabled);
        if (mounted.current) {
          logD(TAG, "Add key event handler");
          // case of playerwebrtc test
          // Navigation provider is unmounted after each play
          document.documentElement.addEventListener("keydown", onKeyDownEnable);
        }
        logD(TAG, "Remove mouse event handlers");
        MouseEvents.forEach((evt) =>
          document.documentElement.removeEventListener(
            evt,
            onMouseEventDisable as any
          )
        );
      };
    }
    return () => {
      logD(TAG, "Nop");
    };
  }, [enabled, onKeyDownEnable, onMouseEventDisable]);

  useEffect(() => {
    logD(TAG, ">>In use effect");
    if (!logger.configured && loggingConfig) {
      logger.configure(loggingConfig);
    }
    if (defaultEnabled) {
      enable();
    } else {
      logD(TAG, "Add key event handler");
      document.documentElement.addEventListener("keydown", onKeyDownEnable);
    }
    inputUI.setupUIGamepad();
    return () => {
      logD(TAG, "<<Out use effect");
      JsSpatialNavigation.uninit();
      inited.current = false;
      setEnabled(false);
      inputUI.destroyGamepadUI();

      logD(TAG, "Remove mouse event handlers");
      MouseEvents.forEach((evt) =>
        document.documentElement.removeEventListener(
          evt,
          onMouseEventDisable as any
        )
      );
      logD(TAG, "Remove key event handler");
      document.documentElement.removeEventListener("keydown", onKeyDownEnable);
    };
  }, [defaultEnabled, loggingConfig]);

  const inited = useRef(false);
  return (
    <SpatialNavigationContext.Provider
      value={{
        config,
        active: active && enabled,
        pause,
        resume,
        enabled,
        teardown: JsSpatialNavigation.uninit,
      }}
    >
      {children}
    </SpatialNavigationContext.Provider>
  );
};

export const useSpatialNavigation = () => useContext(SpatialNavigationContext);
export default SpatialNavigationProvider;

export const useForceFocusOnMount = (
  path: string,
  opts?: { preventScroll: boolean }
) => {
  const mounted = useRef(false);
  const { active, resume, enabled } = useSpatialNavigation();
  useEffect(() => {
    if (active && !mounted.current) {
      mounted.current = true;
      setTimeout(() => {
        resume();
        if (enabled) {
          focusSectionWithPath(path, opts);
        }
      }, 500);
    }
  }, [active, opts, path, resume]);
};

//FIXME remove once GamePlayPage has been refactored
export declare type WithSpatialNavigationInjectedProps = {
  resumeSpatialNavigation: () => void;
  pauseSpatialNavigation: () => void;
  spatialNavigationActive: boolean;
};

const withSpatialNavigation = <
  TOriginalProps extends WithSpatialNavigationInjectedProps
>(
  Component: React.ComponentType<TOriginalProps>
) => {
  const renderFn = (props: TOriginalProps) => {
    const {
      resume: resumeSpatialNavigation,
      pause: pauseSpatialNavigation,
      active: spatialNavigationActive,
    } = useSpatialNavigation();
    return (
      <Component
        {...props}
        resumeSpatialNavigation={resumeSpatialNavigation}
        pauseSpatialNavigation={pauseSpatialNavigation}
        spatialNavigationActive={spatialNavigationActive}
      />
    );
  };
  return renderFn as React.FunctionComponent<
    Omit<TOriginalProps, "resumeSpatialNavigation">
  >;
};
export { withSpatialNavigation };
